/**
 * FilterSheet — Modal-based filter panel for vehicle search.
 * Uses local draft state; commits to explore store on Apply.
 */
import { useEffect, useState, useCallback } from 'react'
import { View, Text, ScrollView as RNScrollView, TextInput as RNTextInput } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { X } from '@/components/ui/icons'
import { Chip } from '../ui/Chip'
import { Checkbox } from '../ui/Checkbox'
import { TrackedNativePressable as RNPressable } from '../ui/TrackedNativePressable'
import { useExploreStore } from '../../lib/stores/explore.store'
import { useSemanticColors, INTERACTION } from '../../lib/theme/tokens'
import { COLORS, FONTS, SPACING, RADIUS } from '../../lib/brand'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { MARKET_TRANSMISSIONS } from '../../lib/utils/transmission'
import { productTelemetryService } from '../../lib/services/product-telemetry.service'
import type { TransmissionType } from '../../lib/types/vehicle.types'
import { dollarsToCents } from '../../lib/utils/format'
import { PRIORITY_VEHICLE_FEATURES } from '../../lib/utils/vehicle-features'

const TRANSMISSION_OPTIONS = MARKET_TRANSMISSIONS

export function FilterSheet() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { isDesktop } = useBreakpoint()

  const isOpen = useExploreStore((s) => s.isFilterSheetOpen)
  const storeTransmission = useExploreStore((s) => s.transmission)
  const storeMinPrice = useExploreStore((s) => s.minPriceCents)
  const storeMaxPrice = useExploreStore((s) => s.maxPriceCents)
  const storeMinYear = useExploreStore((s) => s.minYear)
  const storeInstantBook = useExploreStore((s) => s.isInstantBook)
  const storeRequiredFeatures = useExploreStore((s) => s.requiredFeatures)
  const setFilterSheetOpen = useExploreStore((s) => s.setFilterSheetOpen)
  const setTransmission = useExploreStore((s) => s.setTransmission)
  const setPriceRange = useExploreStore((s) => s.setPriceRange)
  const setMinYear = useExploreStore((s) => s.setMinYear)
  const setInstantBook = useExploreStore((s) => s.setInstantBook)
  const setRequiredFeatures = useExploreStore((s) => s.setRequiredFeatures)
  const resetFilters = useExploreStore((s) => s.resetFilters)

  // ── Draft state ──────────────────────────────────────────────────────────
  const [draftTransmission, setDraftTransmission] = useState<TransmissionType | null>(null)
  const [draftMinPrice, setDraftMinPrice] = useState('')
  const [draftMaxPrice, setDraftMaxPrice] = useState('')
  const [draftMinYear, setDraftMinYear] = useState('')
  const [draftInstantBook, setDraftInstantBook] = useState(false)
  const [draftRequiredFeatures, setDraftRequiredFeatures] = useState<string[]>([])

  // Copy store values into draft when modal opens
  useEffect(() => {
    if (isOpen) {
      setDraftTransmission(storeTransmission)
      setDraftMinPrice(storeMinPrice != null ? String(storeMinPrice / 100) : '')
      setDraftMaxPrice(storeMaxPrice != null ? String(storeMaxPrice / 100) : '')
      setDraftMinYear(storeMinYear != null ? String(storeMinYear) : '')
      setDraftInstantBook(storeInstantBook === true)
      setDraftRequiredFeatures(storeRequiredFeatures)
    }
  }, [
    isOpen,
    storeTransmission,
    storeMinPrice,
    storeMaxPrice,
    storeMinYear,
    storeInstantBook,
    storeRequiredFeatures,
  ])

  const close = useCallback(() => setFilterSheetOpen(false), [setFilterSheetOpen])

  const handleApply = useCallback(() => {
    productTelemetryService.recordEvent('filter', 'filter.applied', {
      elementId: 'filter-sheet.apply',
      elementType: 'button',
      metadata: {
        hasTransmission: Boolean(draftTransmission),
        hasMinPrice: Boolean(draftMinPrice),
        hasMaxPrice: Boolean(draftMaxPrice),
        hasMinYear: Boolean(draftMinYear),
        instantBook: draftInstantBook,
        requiredFeatures: draftRequiredFeatures,
      },
    })
    setTransmission(draftTransmission)
    const minCents = draftMinPrice ? dollarsToCents(Number(draftMinPrice)) : null
    const maxCents = draftMaxPrice ? dollarsToCents(Number(draftMaxPrice)) : null
    setPriceRange(
      minCents && !isNaN(minCents) ? minCents : null,
      maxCents && !isNaN(maxCents) ? maxCents : null,
    )
    const year = draftMinYear ? Number(draftMinYear) : null
    setMinYear(year && !isNaN(year) ? year : null)
    setInstantBook(draftInstantBook ? true : null)
    setRequiredFeatures(draftRequiredFeatures)
    close()
  }, [
    draftTransmission,
    draftMinPrice,
    draftMaxPrice,
    draftMinYear,
    draftInstantBook,
    draftRequiredFeatures,
    setTransmission,
    setPriceRange,
    setMinYear,
    setInstantBook,
    setRequiredFeatures,
    close,
  ])

  const handleClear = useCallback(() => {
    productTelemetryService.recordEvent('filter', 'filter.cleared', {
      elementId: 'filter-sheet.clear',
      elementType: 'button',
    })
    resetFilters()
    close()
  }, [resetFilters, close])

  return (
    <AppModal
      visible={isOpen}
      animationType="slide"
      transparent
      onRequestClose={close}
      testID="filter-sheet.modal"
    >
      {/*
        Scrim DETRAS, no alrededor. Mismo defecto que MapClusterSheet: dos
        `Pressable` de react-native anidados con `Chip` y `Checkbox` adentro, que
        usan el nuestro (gesture-handler en Android). El ancestro de RN gana el
        toque y el chip nunca se marca -- 320 toques de 126 personas sobre esta
        hoja, y `filter-sheet.instant-book` lo lograron marcar 14 personas en
        toda la historia de la app.

        La forma correcta esta en components/ui/Select.tsx:229-253.
      */}
      <View
        style={{
          flex: 1,
          justifyContent: isDesktop ? 'center' : 'flex-end',
          alignItems: isDesktop ? 'center' : 'stretch',
          padding: isDesktop ? SPACING.xl : 0,
        }}
      >
        <RNPressable
          onPress={close}
          accessibilityRole="button"
          accessibilityLabel={t('explore.location_close')}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        />
        <GlassSheet
          testID="filters.sheet"
          accessibilityLabel={t('explore.filter_title')}
          style={{
            borderTopLeftRadius: RADIUS.xl,
            borderTopRightRadius: RADIUS.xl,
            borderBottomLeftRadius: isDesktop ? RADIUS.xl : 0,
            borderBottomRightRadius: isDesktop ? RADIUS.xl : 0,
            width: isDesktop ? 440 : undefined,
            maxWidth: isDesktop ? '100%' : undefined,
            maxHeight: isDesktop ? 680 : '85%',
            overflow: 'hidden',
          }}
        >
          {/* ── Header ── */}
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingHorizontal: SPACING.lg,
              paddingTop: isDesktop ? SPACING.xl : SPACING.lg,
              paddingBottom: SPACING.md,
              borderBottomWidth: 1,
              borderBottomColor: colors.border,
            }}
          >
            <Text
              style={{
                fontSize: 20,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('explore.filter_title')}
            </Text>
            <RNPressable
              onPress={close}
              accessibilityRole="button"
              accessibilityLabel={t('explore.location_close')}
              testID="filter-sheet.close"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: colors.surfaceSubtle,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={18} color={colors.textSecondary} strokeWidth={2} />
            </RNPressable>
          </View>

          {/* ── Content ── */}
          {/*
            Sin `flexShrink: 1` esta lista NO se desplaza. El ScrollView se
            dimensiona al alto de su contenido, concluye que entra entero, y el
            contenedor de arriba --`maxHeight: '85%'` con `overflow: hidden'`--
            recorta lo que sobra sin dejar forma de llegar a ello. Mismo defecto
            que tenia MapClusterSheet con 16 carros.

            `flexShrink` y no `flex`: con pocos filtros la hoja abraza su
            contenido en vez de estirarse al 85% de la pantalla.
          */}
          <RNScrollView
            style={{ flexShrink: 1 }}
            contentContainerStyle={{ paddingHorizontal: SPACING.lg, paddingBottom: SPACING.lg }}
            showsVerticalScrollIndicator={false}
          >
            {/* Transmission */}
            <View style={{ marginTop: SPACING.lg }}>
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  marginBottom: SPACING.sm,
                }}
              >
                {t('explore.filter_transmission')}
              </Text>
              {isDesktop ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
                  {TRANSMISSION_OPTIONS.map((tx) => (
                    <Chip
                      key={tx}
                      label={t(`explore.transmission.${tx}`)}
                      selected={draftTransmission === tx}
                      onPress={() => setDraftTransmission(draftTransmission === tx ? null : tx)}
                    />
                  ))}
                </View>
              ) : (
                <RNScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: SPACING.sm }}
                >
                  {TRANSMISSION_OPTIONS.map((tx) => (
                    <Chip
                      key={tx}
                      label={t(`explore.transmission.${tx}`)}
                      selected={draftTransmission === tx}
                      onPress={() => setDraftTransmission(draftTransmission === tx ? null : tx)}
                    />
                  ))}
                </RNScrollView>
              )}
            </View>

            {/* High-value inclusions */}
            <View style={{ marginTop: SPACING.lg }}>
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  marginBottom: SPACING.xs,
                }}
              >
                {t('explore.filter_included_services')}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: colors.textMuted,
                  marginBottom: SPACING.sm,
                }}
              >
                {t('explore.filter_included_services_hint')}
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
                {PRIORITY_VEHICLE_FEATURES.map((feature) => {
                  const selected = draftRequiredFeatures.includes(feature)
                  return (
                    <Chip
                      key={feature}
                      label={t(`listing.vehicle_features.${feature}`)}
                      selected={selected}
                      variant={selected ? 'filled' : 'outlined'}
                      color={feature === 'diesel_included' ? 'info' : 'success'}
                      onPress={() =>
                        setDraftRequiredFeatures((current) =>
                          current.includes(feature)
                            ? current.filter((item) => item !== feature)
                            : [...current, feature],
                        )
                      }
                    />
                  )
                })}
              </View>
            </View>

            {/* Price Range */}
            <View style={{ marginTop: SPACING.lg }}>
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  marginBottom: SPACING.sm,
                }}
              >
                {t('explore.filter_price_range')}
              </Text>
              <View style={{ flexDirection: 'row', gap: SPACING.md }}>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      marginBottom: SPACING.xs,
                    }}
                  >
                    {t('explore.filter_min_price')}
                  </Text>
                  <RNTextInput
                    value={draftMinPrice}
                    onChangeText={setDraftMinPrice}
                    keyboardType="number-pad"
                    placeholder="$0"
                    placeholderTextColor={colors.textMuted}
                    accessibilityLabel={t('explore.filter_min_price')}
                    testID="filter-sheet.min-price"
                    style={{
                      minHeight: INTERACTION.fieldHeight,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderRadius: RADIUS.md,
                      paddingHorizontal: SPACING.md,
                      paddingVertical: SPACING.md,
                      fontSize: 16,
                      fontFamily: FONTS.body,
                      color: colors.textPrimary,
                      backgroundColor: colors.surfaceSubtle,
                    }}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      marginBottom: SPACING.xs,
                    }}
                  >
                    {t('explore.filter_max_price')}
                  </Text>
                  <RNTextInput
                    value={draftMaxPrice}
                    onChangeText={setDraftMaxPrice}
                    keyboardType="number-pad"
                    placeholder="$999"
                    placeholderTextColor={colors.textMuted}
                    accessibilityLabel={t('explore.filter_max_price')}
                    testID="filter-sheet.max-price"
                    style={{
                      minHeight: INTERACTION.fieldHeight,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderRadius: RADIUS.md,
                      paddingHorizontal: SPACING.md,
                      paddingVertical: SPACING.md,
                      fontSize: 16,
                      fontFamily: FONTS.body,
                      color: colors.textPrimary,
                      backgroundColor: colors.surfaceSubtle,
                    }}
                  />
                </View>
              </View>
            </View>

            {/* Min Year */}
            <View style={{ marginTop: SPACING.lg }}>
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  marginBottom: SPACING.sm,
                }}
              >
                {t('explore.filter_year')}
              </Text>
              <RNTextInput
                value={draftMinYear}
                onChangeText={setDraftMinYear}
                keyboardType="number-pad"
                placeholder="2020"
                placeholderTextColor={colors.textMuted}
                maxLength={4}
                accessibilityLabel={t('explore.filter_year')}
                testID="filter-sheet.min-year"
                style={{
                  minHeight: INTERACTION.fieldHeight,
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderRadius: RADIUS.md,
                  paddingHorizontal: SPACING.md,
                  paddingVertical: SPACING.md,
                  fontSize: 16,
                  fontFamily: FONTS.body,
                  color: colors.textPrimary,
                  backgroundColor: colors.surfaceSubtle,
                  maxWidth: 160,
                }}
              />
            </View>

            {/* Instant Book */}
            <View style={{ marginTop: SPACING.lg }}>
              <Checkbox
                checked={draftInstantBook}
                onChange={setDraftInstantBook}
                label={t('explore.filter_instant_book')}
                testID="filter-sheet.instant-book"
              />
            </View>
          </RNScrollView>

          {/* ── Footer ── */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: SPACING.lg,
              paddingTop: SPACING.md,
              paddingBottom: isDesktop ? SPACING.lg : Math.max(insets.bottom, SPACING.md),
              borderTopWidth: 1,
              borderTopColor: colors.border,
            }}
          >
            <RNPressable
              onPress={handleClear}
              accessibilityRole="button"
              accessibilityLabel={t('explore.filter_reset')}
              testID="filter-sheet.clear"
              style={{ paddingVertical: SPACING.sm, paddingHorizontal: SPACING.md }}
            >
              <Text
                style={{
                  fontSize: 15,
                  fontFamily: FONTS.body,
                  color: colors.textAccent,
                }}
              >
                {t('explore.filter_reset')}
              </Text>
            </RNPressable>
            <View style={{ flex: 1, maxWidth: 200 }}>
              <RNPressable
                onPress={handleApply}
                accessibilityRole="button"
                accessibilityLabel={t('explore.filter_apply')}
                testID="filter-sheet.apply"
                style={{
                  height: 48,
                  borderRadius: RADIUS.lg,
                  backgroundColor: COLORS.coral.dark,
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingHorizontal: SPACING.lg,
                }}
              >
                <Text
                  style={{
                    fontSize: 15,
                    fontFamily: FONTS.bodyBold,
                    color: '#FFFFFF',
                    textAlign: 'center',
                  }}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.78}
                >
                  {t('explore.filter_apply')}
                </Text>
              </RNPressable>
            </View>
          </View>
        </GlassSheet>
      </View>
    </AppModal>
  )
}
